import { useEffect, useRef, useState } from 'react';
import './LocationPicker.css';

function LocationPicker({ onLocationSelect, initialLocation }) {
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);

  useEffect(() => {
    // Wait for Google Maps to load
    const initMap = () => {
      if (!window.google) {
        setTimeout(initMap, 100);
        return;
      }

      // Default location (India center)
      const defaultLocation = { lat: 20.5937, lng: 78.9629 };

      // Initialize map
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: initialLocation || defaultLocation,
        zoom: initialLocation ? 15 : 5,
        mapTypeControl: false,
      });

      setMap(mapInstance);

      // Initialize marker
      const markerInstance = new window.google.maps.Marker({
        map: mapInstance,
        draggable: true,
        visible: !!initialLocation,
        position: initialLocation || defaultLocation,
      });

      setMarker(markerInstance);

      // Initialize autocomplete
      const autocomplete = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        { types: ['establishment', 'geocode'] }
      );

      autocomplete.bindTo('bounds', mapInstance);

      // Handle place selection from autocomplete
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (!place.geometry || !place.geometry.location) {
          return;
        }

        // Update map and marker
        mapInstance.setCenter(place.geometry.location);
        mapInstance.setZoom(15);
        markerInstance.setPosition(place.geometry.location);
        markerInstance.setVisible(true);

        // Extract location details
        extractPlaceDetails(place);
      });

      // Handle marker drag
      markerInstance.addListener('dragend', () => {
        const position = markerInstance.getPosition();
        reverseGeocode(position);
      });

      // Handle map click
      mapInstance.addListener('click', (e) => {
        markerInstance.setPosition(e.latLng);
        markerInstance.setVisible(true);
        reverseGeocode(e.latLng);
      });
    };

    initMap();
  }, [initialLocation]);

  const extractPlaceDetails = (place) => {
    let country = '';
    let state = '';
    let city = '';
    let locationName = place.formatted_address || '';

    // Parse address components
    place.address_components?.forEach((component) => {
      const types = component.types;

      if (types.includes('country')) {
        country = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.long_name;
      }
      if (types.includes('locality') || types.includes('administrative_area_level_2')) {
        city = component.long_name;
      }
    });

    const locationData = {
      country,
      state,
      city,
      locationName,
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    setSelectedPlace(locationData);

    if (onLocationSelect) {
      onLocationSelect(locationData);
    }
  };

  const reverseGeocode = (latLng) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results[0]) {
        extractPlaceDetails(results[0]);
      }
    });
  };

  return (
    <div className="location-picker">
      <div className="location-picker-search">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search for a venue or address..."
          className="location-picker-input"
        />
      </div>

      <div ref={mapRef} className="location-picker-map"></div>

      {selectedPlace && (
        <div className="location-picker-details">
          <div className="location-picker-info">
            <strong>Selected Location:</strong>
            <p>{selectedPlace.locationName}</p>
            <small>{selectedPlace.city}, {selectedPlace.state}, {selectedPlace.country}</small>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationPicker;
